<template>
  <div key="order-summary" class="order-summary">
    <div
      v-if="!isElementHidden(HIDDEN_ELEMENTS.ORDER_SUMMARY)"
      class="order-summary__header text-center w-100 pt-3"
    >
      <div :class="[{ '--fallback': !product.image }, 'order-summary__image']">
        <img
          :src="
            product.image ? getImageUrl(product.image) : getImageUrl(appIcon)
          "
        />
      </div>
      <h2 class="mb-2 mt-1">
        {{ product.title }}
      </h2>
    </div>

    <list v-if="order" boxed class="order-summary__amount-box px-2 pt-1">
      <list-item @click="() => (showSummary = !showSummary)">
        <div class="v-list-item__title">
          <span class="text-grey-60 text-small mb-2">
            {{ $t("components.checkout.orderSummary.amount") }}
          </span>

          <div class="order-summary__amount">
            <template v-if="zeroAmountOrderHasTrial">
              <div>
                {{ primaryItem.trial_period_days }}
                {{ $t("components.checkout.orderSummary.daysFree") }}
              </div>
            </template>

            <template v-else>
              {{ order.initial.symbol || "$"
              }}{{ formatAmount(order.initial.final_amount) }}
              {{ order.initial.currency }}

              <template v-if="isSingleItemOrder && primaryItem.recurring">
                {{
                  productInterval({
                    interval: primaryItem.recurring.interval,
                    intervalCount: primaryItem.recurring.interval_count,
                    expiryUnits: primaryItem.recurring.expiry_units
                  })
                }}
              </template>
            </template>
          </div>
          <div
            v-if="
              order.upcoming &&
                !(
                  isSingleItemOrder &&
                  !zeroAmountOrderHasTrial &&
                  !isDiscountApplied(order)
                )
            "
            class="text-grey-70 text-small"
          >
            <div v-for="(item, i) in order.upcoming.line_items" :key="i">
              <span v-if="i === 0">{{
                $t("components.checkout.orderSummary.then")
              }}</span>
              <span v-else>{{
                $t("components.checkout.orderSummary.and")
              }}</span>
              {{ order.upcoming.symbol || "$"
              }}{{ formatAmount(item.final_amount) }}
              {{ order.upcoming.currency }}
              <template v-if="item.recurring">
                {{
                  productInterval({
                    interval: item.recurring.interval,
                    intervalCount: item.recurring.interval_count,
                    expiryUnits: item.recurring.expiry_units
                  })
                }}

                <template v-if="item.trial_period_days && !isSingleItemOrder">
                  {{ $t("components.checkout.orderSummary.after") }}
                  {{ item.trial_period_days }}
                  {{ $t("components.checkout.orderSummary.daysFreeTrial") }}
                </template>
              </template>
            </div>
          </div>
        </div>

        <template slot="right">
          <v-button ghost round>
            <v-icon
              class="mr-0"
              :icon="showSummary ? 'expand_less' : 'expand_more'"
            />
          </v-button>
        </template>

        <template v-slot:row v-if="showSummary">
          <div class="w-100">
            <order-preview-breakdown :order="order.initial" />

            <div v-if="order.upcoming" class="mt-3 w-100">
              <span class="v-list-item__title text-grey-60 text-small">
                {{ $t("components.checkout.orderSummary.thenHeading") }}
              </span>
              <div class="order">
                <div
                  v-for="(line, i) in orderedBySoonest(
                    order.upcoming.line_items
                  )"
                  :key="i"
                  :class="[
                    { '--last': i + 1 === order.upcoming.line_items.length },
                    'order__line-item --vertical'
                  ]"
                >
                  <div class="order__line-item--left">
                    {{ line.description }}:
                  </div>

                  <div class="order__line-item--right">
                    <template v-if="line.trial_period_days">
                      {{
                        $tc(
                          "components.checkout.orderSummary.inNDays",
                          line.trial_period_days,
                          { n: line.trial_period_days }
                        )
                      }},
                    </template>
                    <template v-else-if="isPresent(line.recurring)">
                      {{ $t("components.checkout.orderSummary.in") }}
                      {{ line.recurring.interval_count }}
                      {{
                        pluralize(
                          line.recurring.interval,
                          line.recurring.interval_count
                        )
                      }},
                    </template>

                    {{ line.symbol || "$"
                    }}{{ formatAmount(line.final_amount) }}
                    {{ line.currency }}

                    <template
                      v-if="
                        line.recurring &&
                          line.recurring.interval &&
                          shouldShowUpcomingInterval(line)
                      "
                    >
                      {{ /* Here we show 1 less expiryUnit to ensure we are accurately presenting only the remaining payments */ }}
                      {{
                        productInterval({
                          interval: line.recurring.interval,
                          intervalCount: line.recurring.interval_count,
                          expiryUnits:
                            line.recurring.expiry_units -
                            line.recurring.interval_count
                        })
                      }}
                    </template>
                    <template v-if="!shouldShowUpcomingInterval(line)">
                      {{ $t("components.checkout.orderSummary.once") }}
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </list-item>

      <slot />

      <list-item
        v-if="shouldUseIAPForProduct(product)"
        :align-start="isGuest"
        no-padding-top
        no-padding-right
        class="py-1"
      >
        <template slot="left">
          <div :class="['order-summary__left', { 'mt-2': isGuest }]">
            <span>
              {{ $t("components.checkout.orderSummary.email") }}
            </span>
          </div>
        </template>
        <div class="v-list-item__title p-1">
          <template v-if="isPresent(currentUser)">
            <span class="pl-1">
              {{ email }}
            </span>
          </template>

          <template v-else-if="isGuest && shouldUseIAPForProduct(product)">
            <template v-if="readonly">
              <span class="pl-1">
                {{ email }}
              </span>
            </template>
            <template v-else>
              <v-input
                :value="email"
                autofocus
                placeholder="Enter your email"
                type="email"
                name="email"
                :validator="$v.emailValidator"
                @input="val => $emit('input', val)"
              />

              <email-field-cta
                :class="{ 'mt-2': emailExists }"
                :email-exists="emailExists"
              />
            </template>
          </template>
        </div>
      </list-item>
    </list>

    <list v-if="isLoading && !order" boxed class="px-2">
      <list-item>
        <template slot="left">
          <div class="v-list-item__title">
            <span class="text-grey-60 text-small mb-2">
              {{ $t("components.checkout.orderSummary.amount") }}
            </span>

            <div class="order-summary__amount">
              <placeholder-rect width="120px" class="m-0" height="16px" />
            </div>
          </div>
        </template>
      </list-item>
    </list>

    <slot name="after" />
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import { validationMixin } from "vuelidate"
import { required, email } from "vuelidate/lib/validators"

import { isPresent, getImageUrl, pluralize } from "@/lib/utils"
import { HIDDEN_ELEMENTS } from "@/lib/constants"
import {
  shouldUseIAPForProduct,
  productInterval,
  isDiscountApplied,
  formatAmount
} from "@/lib/checkout-utils"

import List from "@/components/List"
import ListItem from "@/components/ListItem"
import vInput from "@/components/forms/vInput"
import EmailFieldCta from "@/components/checkout/EmailFieldCta"

export default {
  mixins: [validationMixin],

  components: {
    List,
    ListItem,
    vInput,
    EmailFieldCta,
    OrderPreviewBreakdown: () =>
      import("@/components/checkout/OrderPreviewBreakdown"),
    PlaceholderRect: () => import("@/components/PlaceholderRect")
  },

  model: {
    prop: "email",
    event: "input"
  },

  props: {
    email: { type: String, default: "" },
    product: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    emailExists: { type: Boolean, default: false },
    order: { type: Object, default: () => null },
    isLoading: { type: Boolean, default: false }
  },

  data() {
    return {
      emailValidator: this.email,
      showSummary: false,

      HIDDEN_ELEMENTS
    }
  },

  computed: {
    ...mapGetters("auth", ["isGuest", "currentUser"]),
    ...mapGetters("ui", ["isElementHidden"]),
    ...mapGetters(["appIcon"]),

    isSingleItemOrder() {
      return this.order?.initial?.line_items?.length === 1
    },

    primaryItem() {
      return this.order?.initial?.line_items[0] || {}
    },

    zeroAmountOrderHasTrial() {
      return (
        this.isSingleItemOrder &&
        this.order.initial.final_amount_cents === 0 &&
        !!this.primaryItem.trial_period_days
      )
    }
  },

  validations: {
    emailValidator: {
      email,
      required
    }
  },

  watch: {
    email: {
      immediate: true,
      handler: function() {
        this.emailValidator = this.email
        this.$v.emailValidator.$touch()
      }
    }
  },

  methods: {
    isPresent,
    productInterval,
    shouldUseIAPForProduct,
    getImageUrl,
    isDiscountApplied,
    formatAmount,
    pluralize,

    orderedBySoonest(lineItems) {
      return lineItems.toSorted((a, b) => {
        if (a.trial_period_days && !b.trial_period_days) {
          return -1
        } else if (!a.trial_period_days && b.trial_period_days) {
          return 1
        } else if (a.trial_period_days && b.trial_period_days) {
          return a.trial_period_days - b.trial_period_days
        } else {
          return 0
        }
      })
    },

    shouldShowUpcomingInterval(lineItem) {
      const hasLimitedPayments = lineItem.recurring.expiry_units > 0

      if (hasLimitedPayments) {
        // Given a product that bills fortnightly for 1 month; we don't want to show "every 2 weeks for 2 weeks"
        // on the upcoming payment
        const numberOfRemainingPayments =
          lineItem.recurring.expiry_units / lineItem.recurring.interval_count -
          1

        return numberOfRemainingPayments > 1
      } else {
        return true
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.order-summary {
  .order-summary__image {
    img {
      border: 3px solid $color-ui--white;
      box-shadow: $box-shadow--medium;
      border-radius: var(--input-border-radius);
      max-height: $size--12;
    }

    &.--fallback {
      img {
        max-width: $size--11;
      }
    }
  }
  .order-summary__amount-box {
    .v-list-item__right {
      align-self: flex-start;
    }
  }

  .order-summary__amount {
    font-size: $font-size--base;
    font-weight: $font-weight--semi-bold;
    margin-top: 0.5rem;
    margin-bottom: 0.25rem;
  }

  .v-list-item__row {
    padding-left: $size--5;
    padding-right: $size--3;
  }

  .v-list-item__center {
    overflow: visible;
  }

  .order-summary__left {
    width: 5rem;
    min-width: 5rem;
    max-width: 6rem;
    flex-grow: 0;
    padding-top: 0;
    display: flex;
    align-items: center;
    height: 100%;

    span {
      font-size: $font-size--small;
      color: $color-ui--grey-60;
    }
  }

  .flex-shrink-0 {
    flex-shrink: 0;
  }

  .w-100 {
    width: 100%;
  }

  .v-list-item__center {
    flex-grow: 1;
  }

  .v-list-item {
    &:hover {
      background: $color-ui--white;
    }
  }

  .v-list__box {
    margin: 0;
    box-shadow: none;
    border: 1px solid $color-ui--grey-20;
  }
}
</style>
